<template>
	<v-container
		mt-4
		pa-0
		grid-list-xl
	>
		<v-layout
			row
			wrap
			ma-0
			justify-center
			align-center
		>
			<v-flex
				pa-0
				xs12
			>
				<v-card flat>
					<div v-show="loading || loadingProgress > 0">
						<v-progress-linear
							v-model="loadingProgress"
							:active="true"
							class="ma-0"
						/>
						<div class="pt-2 text-xs-center">
							<v-progress-circular
								v-show="loading"
								:size="25"
								color="primary"
								indeterminate
								class="mr-2"
							/>
							{{ progressText }}
						</div>
					</div>
					<ArticleQueue
						:error-items="errorItems"
						:success-items="addedItems"
						@retry-success="retrySuccess"
					/>
					<ArticleForm
						v-if="!loading"
						:class="{ 'pt-0': articleQueueVisible }"
						@save-article="addArticles"
						@close="closeArticleForm()"
					/>
					<div v-else class="text-xs-center">
						<v-btn
							flat
							color="error"
							class="mb-4"
							@click="cancelQueue"
						>
							Cancel
						</v-btn>
					</div>
				</v-card>
			</v-flex>
		</v-layout>
		<Confirm ref="confirm" />
	</v-container>
</template>

<script>
import StoreMethods from '@/mixins/StoreMethods';
import ArticleForm from '@/components/Article/ArticleForm';
import ArticleQueue from '@/components/Article/ArticleQueue';
import Confirm from '@/components/Confirm';
import { Article } from '@/models/Article';
import PQueue from 'p-queue';

export default {
	components: {
		ArticleForm,
		ArticleQueue,
		Confirm,
	},
	mixins: [StoreMethods],
	props: {
		extended: { type: Boolean, default: false },
	},
	data() {
		return {
			addedItems: [],
			errorItems: [],
			loading: false,
			loadingProgress: 0,
			urlCount: 0,
			isSingle: false,
			queue: new PQueue({ concurrency: 2 }),
		};
	},
	computed: {
		articles() {
			return this.$store.get('articles');
		},
		reports() {
			return this.$store.get('reports/all').models;
		},
		currentReport() {
			return this.$store.get('reports/currentReport');
		},
		currentProject() {
			return this.$store.get('projects/currentProject');
		},
		reportPublications() {
			return this.$store.get('reportPublications');
		},
		checkSaveComplete() {
			if (this.urlCount !== (this.addedItems.length + this.errorItems.length)) {
				return false;
			}
			let complete = true;
			this.addedItems.forEach((article) => {
				if (article.saving) {
					complete = false;
				}
			});

			return complete;
		},
		articleQueueVisible() {
			if (this.addedItems.length > 0 || this.errorItems.length > 0) {
				return true;
			}
			return false;
		},
		progressText() {
			if (this.loadingProgress === 0) {
				return `0/${this.urlCount} articles processed.`;
			}
			const completedItems = this.addedItems.length + this.errorItems.length;
			return `${completedItems}/${this.urlCount} articles processed.`;
		},
	},
	watch: {
		checkSaveComplete(val) {
			if (val) {
				this.loading = false;
				this.onSaveComplete(this.currentReport.id);
			}
		},
	},
	methods: {
		addArticles(custom, urlsList, isSingle, importance) {
			const processingURLs = this.removeDuplicateURLS(urlsList);
			const reports = this.reports;
			this.loading = true;
			this.loadingProgress = 0;
			this.errorItems = [];
			this.addedItems = [];
			this.urlCount = processingURLs.length;
			this.isSingle = isSingle;
			const urls = this.filterDuplicateURLsFromReport(processingURLs, custom);

			// If all of the articles added already exist in the report we are done
			if (urls.length === 0) {
				this.loading = false;
				return;
			}

			for (let i = 0; i < urls.length; i++) {
				if (urls[i].length > 0) {
					const model = new Article();
					model.url = urls[i].trim();
					model.reportId = this.currentReport.id;
					model.projectId = this.currentProject.id;
					model.customData = custom;
					model.important = importance;
					model.justAdded = true;

					this.queue.add(() => model.save().then((r) => {
						this.articleAdded(model);
					}).catch((e) => {
						if (e.response.response.data.alreadyExists) {
							const filteredReports = reports.filter(report => e.response.response.data.alreadyExists.includes(report.id));
							const s = (filteredReports.length === 1 ? '' : 's');
							model.set('tag', 'warning');
							model.set('message', `Article with this URL has already been added to following report${s}: ` + filteredReports.map((report) => report.displayName).join(', ') + '.');
							model.set('addAnyways', true);
						}
						else {
							model.set('tag', 'error');
							model.set('message', 'Failed to retrieve article.');
						}

						if (this.isSingle) {
							model.set('singleEdit', true);
						}
						this.errorItems.push(model);
					}).finally(() => {
						this.loadingProgress += 100 / this.urlCount;
					}));
				}
			}
		},
		retrySuccess(article) {
			this.addedItems.push(article);
			article.justAddedTimeout();
			this.updateReportPublications();
			this.errorItems.forEach((item) => {
				if (item.url === article.url) {
					const deleteIndex = this.errorItems.indexOf(item);
					this.errorItems.splice(deleteIndex, 1);
				}
			});
			// Push event for add page, emit for quick add
			if (article.singleEdit) {
				this.$router.push({ name: 'ArticlesList', params: { reportId: this.currentReport.id, showEdit: article } });
				this.$emit('single-edit', article);
				this.isSingle = false;
			}
		},
		onSaveComplete(reportId) {
			this.updateReportPublications();
			if (this.isSingle) {
				this.singleArticleShowEdit(reportId);
				return;
			}
			const articleMessage = this.addedItems.length === 1 ? 'Article' : `${this.addedItems.length} articles`;
			if (this.errorItems.length > 0) {
				// const articleErrorMessage = this.errorItems.length === 1
				// 	? 'article' : `${this.errorItems.length} articles`;
				// this.$notify('error', `Failed to add ${articleErrorMessage}`);
			}
			else if (this.$route.name !== 'ArticlesList') {
				this.$notify('success', `${articleMessage} added.`,
					{ name: 'ArticlesList', params: { reportId } });
			}
			else {
				this.$notify('success', `${articleMessage} added.`);
			}
		},
		singleArticleShowEdit(reportId) {
			if (this.errorItems.length > 0) {
				// this.$notify('error', 'Failed to add article.');
				return;
			}
			const articles = this.articles;
			const article = articles[articles.length - 1];
			// Push event for add page, emit for quick add
			this.$router.push({ name: 'ArticlesList', params: { reportId, showEdit: article } });
			this.$emit('single-edit', article);
			this.isSingle = false;
			this.$notify('success', 'Article added.');
		},
		removeDuplicateURLS(urls) {
			const uniqueURLs = urls.filter((item, pos, self) => self.indexOf(item) === pos);
			return uniqueURLs;
		},
		filterDuplicateURLsFromReport(urls, customData) {
			const articlesURLs = this.articles.map(article => article.url);
			const filteredURLs = urls;
			for (let i = filteredURLs.length - 1; i >= 0; i--) {
				if (articlesURLs.includes(filteredURLs[i].trim())) {
					const model = new Article();
					model.url = filteredURLs[i];
					// Properties if users want to add anyways
					model.reportId = this.currentReport.id;
					model.projectId = this.currentProject.id;
					model.customData = customData;
					model.set('justAdded', true);
					// Custom properties for article queue handling
					model.set('tag', 'warning');
					model.set('message', 'Article with this URL has already been added.');
					model.set('addAnyways', true);
					if (this.isSingle) {
						model.set('singleEdit', true);
					}
					this.errorItems.push(model);
					filteredURLs.splice(i, 1);
					this.loadingProgress += 100 / this.urlCount;
				}
			}
			return filteredURLs;
		},
		articleAdded(article) {
			this.articles.push(article);
			article.justAddedTimeout();
			// Article not fetched correctly if no title or body
			if (!article.title || !article.body) {
				article.set('tag', 'info');
				article.set('message', 'Article was not added correctly, try refetching or manually copy in data by clicking edit below.');
				article.set('tagAction', 'refetch');
				if (this.isSingle) {
					article.set('singleEdit', true);
					this.errorItems.push(article);
				}
				else {
					this.errorItems.push(article);
				}
			}
			else {
				this.addedItems.push(article);
			}
		},
		updateReportPublications() {
			const articles = this.articles;
			let refetch = false;
			articles.forEach((article) => {
				const publication = this.reportPublications.find(pub => pub.id === article.publicationId);
				if (!publication) {
					refetch = true;
				}
			});
			if (refetch) {
				this.setStoreReportPublications(this.currentReport.id);
			}
		},
		cancelQueue() {
			this.$refs.confirm
				.createConfirm('Stop processing articles?', 'error')
				.then((answer) => {
					if (!answer) {
						return;
					}
					this.queue.clear();
					this.loading = false;
				});
		},
		closeArticleForm() {
			this.$emit('close');
		},
	},
};
</script>
